import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/AdminLayout/AdminLayout';
import { API_URL, SOCKET_URL } from '../../config/api';
import './SubAdminPage.css';

/* ───── helpers ───── */
const getToken = () => localStorage.getItem('token');

const getFullImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url.replace(/http:\/\/localhost:\d+/, SOCKET_URL);
  if (url.startsWith('/')) return `${SOCKET_URL}${url}`;
  return `${SOCKET_URL}/${url}`;
};

const PERMISSION_FIELDS = [
  { key: 'permDashboard', label: 'Dashboard' },
  { key: 'permNewsLists', label: 'News Lists' },
  { key: 'permSuppliersManage', label: 'Suppliers Manage' },
  { key: 'permSpecialistsManage', label: 'Specialists Manage' },
  { key: 'permProductsManage', label: 'Products Manage' },
  { key: 'permProjectsManage', label: 'Projects Manage' },
  { key: 'permLocationCategory', label: 'Location & Category' },
  { key: 'permVerificationManage', label: 'Verification manage' },
  { key: 'permMembershipPlan', label: 'Membership Plan' },
  { key: 'permAdsPromotion', label: 'Ads & Promotion' },
  { key: 'permMessageInquire', label: 'Message & Inquire' },
  { key: 'permAddBrand', label: 'Add Brand' },
  { key: 'permManageSubscriber', label: 'Manage Subscriber' },
  { key: 'permPaymentHistory', label: 'Payment History' },
];

/* ───── component ───── */
const SubAdminPage = () => {
  // Views: 'list' | 'create' | 'permissions'
  const [view, setView] = useState('list');
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Create form
  const [createForm, setCreateForm] = useState({ email: '', password: '' });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState('');

  // Permissions view
  const [permUser, setPermUser] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [permLoading, setPermLoading] = useState(false);
  const [permError, setPermError] = useState('');

  // Change password modal
  const [changePwUser, setChangePwUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');  
  const [copiedId, setCopiedId] = useState(null);

  // View detail modal
  const [viewUser, setViewUser] = useState(null);

  /* ── fetch sub-admins ── */
  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = getToken();
      if (!token) { setError('Authentication required.'); setLoading(false); return; }
      const headers = { Authorization: `Bearer ${token}` };
      const [r1, r2] = await Promise.all([
        fetch(`${API_URL}/users?adminRole=admin&limit=50&page=1`, { headers }),
        fetch(`${API_URL}/users?adminRole=subadmin&limit=50&page=1`, { headers }),
      ]);
      // Parse both responses independently — don't fail everything if one has an error
      const d1 = r1.ok ? await r1.json() : { users: [] };
      const d2 = r2.ok ? await r2.json() : { users: [] };
      if (!r1.ok && !r2.ok) {
        const errData = await r1.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to load accounts');
      }
      setAccounts([...(d1.users || []), ...(d2.users || [])]);
    } catch (err) {
      setError(err.message || 'Failed to load accounts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  /* ── create sub-admin ── */
  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createForm.email || !createForm.password) { setCreateError('Email and password are required'); return; }
    setCreateLoading(true);
    setCreateError('');
    try {
      const res = await fetch(`${API_URL}/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ email: createForm.email, password: createForm.password, adminRole: 'subadmin', companyName: '', firstName: '', lastName: '', phone: '' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create');
      setSuccess('Sub admin created successfully!');
      setCreateForm({ email: '', password: '' });
      setView('list');
      fetchAccounts();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setCreateError(err.message);
    } finally {
      setCreateLoading(false);
    }
  };

  /* ── toggle status ── */
  const toggleStatus = async (user) => {
    const newStatus = (user.status || 'active').toLowerCase() === 'active' ? 'inactive' : 'active';
    try {
      const res = await fetch(`${API_URL}/users/${user.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error('Failed to update status');
      fetchAccounts();
    } catch (err) { console.error(err); }
  };

  /* ── delete ── */
  const handleDelete = async (user) => {
    if (!window.confirm(`Are you sure you want to delete "${user.companyName || user.email}"?`)) return;
    try {
      await fetch(`${API_URL}/users/${user.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setSuccess('Account deleted');
      fetchAccounts();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { console.error(err); }
  };

  /* ── change password ── */
  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 6) { alert('Password must be at least 6 characters'); return; }
    try {
      const res = await fetch(`${API_URL}/users/${changePwUser.id}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ newPassword }),
      });
      if (!res.ok) throw new Error('Failed to reset password');
      setSuccess('Password changed successfully!');
      setChangePwUser(null);
      setNewPassword('');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { alert(err.message); }
  };

  /* ── copy credential ── */
  const handleCopy = (user) => {
    navigator.clipboard.writeText(`Email: ${user.email}`).then(() => {
      setCopiedId(user.id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  /* ── open permissions view ── */
  const openPermissions = (user) => {
    setPermUser(user);
    const perms = {};
    PERMISSION_FIELDS.forEach(p => { perms[p.key] = user[p.key] || false; });
    setPermissions(perms);
    setPermError('');
    setView('permissions');
  };

  /* ── save permissions ── */
  const handleSavePermissions = async () => {
    setPermLoading(true);
    setPermError('');
    try {
      const res = await fetch(`${API_URL}/users/${permUser.id}/permissions`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify(permissions),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save permissions');
      setSuccess('Permissions updated successfully!');
      setView('list');
      fetchAccounts();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setPermError(err.message);
    } finally {
      setPermLoading(false);
    }
  };

  /* ═══════════════ RENDER ═══════════════ */

  /* ── VIEW: CREATE (Screenshot 2) ── */
  if (view === 'create') {
    return (
      <AdminLayout>
        <div className="rp-page">
          <div className="rp-page-header">
            <div>
              <h1 className="rp-title">Create Sub Admin Account</h1>
              <p className="rp-subtitle">Add, edit, and control sub-admin accounts with customized roles and permissions.</p>
            </div>
          </div>

          <div className="rp-card">
            <h2 className="rp-card-title">Add New Role</h2>

            {createError && <div className="rp-alert rp-alert-error">{createError}</div>}

            <form onSubmit={handleCreate}>
              <div className="rp-form-row">
                <div className="rp-form-group">
                  <label className="rp-label">Email</label>
                  <input type="email" className="rp-input" placeholder="Email" required value={createForm.email}
                    onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} />
                </div>
                <div className="rp-form-group">
                  <label className="rp-label">Password</label>
                  <input type="password" className="rp-input" placeholder="Password" required value={createForm.password}
                    onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} />
                </div>
              </div>

              <div className="rp-form-actions">
                <button type="button" className="rp-btn rp-btn-cancel" onClick={() => { setView('list'); setCreateError(''); }}>Cancel</button>
                <button type="submit" className="rp-btn rp-btn-save" disabled={createLoading}>{createLoading ? 'Saving...' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      </AdminLayout>
    );
  }

  /* ── VIEW: PERMISSIONS (Screenshot 3) ── */
  if (view === 'permissions' && permUser) {
    const displayName = permUser.companyName || `${permUser.firstName || ''} ${permUser.lastName || ''}`.trim() || permUser.email;
    return (
      <AdminLayout>
        <div className="rp-page">
          <div className="rp-page-header">
            <div>
              <h1 className="rp-title">Sub Admin Account</h1>
              <div className="rp-perm-user-info">
                <span className="rp-perm-user-name">{displayName}</span>
                <span className="rp-perm-user-role">Sub Admin</span>
              </div>
            </div>
          </div>

          {permError && <div className="rp-alert rp-alert-error">{permError}</div>}

          {/* Sub-Admin Info */}
          <div className="rp-card">
            <h2 className="rp-card-title">Add New Sub-Admin</h2>
            <div className="rp-form-row">
              <div className="rp-form-group">
                <label className="rp-label">Email</label>
                <input type="email" className="rp-input" placeholder="Email" value={permUser.email} disabled />
              </div>
              <div className="rp-form-group">
                <label className="rp-label">Password</label>
                <input type="password" className="rp-input" placeholder="Password" value="••••••••" disabled />
              </div>
            </div>
          </div>

          {/* Permissions Table */}
          <div className="rp-card">
            <h2 className="rp-card-title">Permission</h2>

            <div className="rp-perm-table">
              <div className="rp-perm-header">
                <span className="rp-perm-col-name">Feature Name</span>
                <span className="rp-perm-col-toggle">Give Permission</span>
              </div>
              {PERMISSION_FIELDS.map((perm) => (
                <div key={perm.key} className="rp-perm-row">
                  <span className="rp-perm-label">{perm.label}</span>
                  <label className="rp-toggle">
                    <input type="checkbox" checked={permissions[perm.key] || false}
                      onChange={(e) => setPermissions({ ...permissions, [perm.key]: e.target.checked })} />
                    <span className="rp-toggle-slider"></span>
                  </label>
                </div>
              ))}
            </div>

            <div className="rp-form-actions" style={{ marginTop: '24px' }}>
              <button type="button" className="rp-btn rp-btn-cancel" onClick={() => setView('list')}>Cancel</button>
              <button type="button" className="rp-btn rp-btn-save" disabled={permLoading}
                onClick={handleSavePermissions}>{permLoading ? 'Saving...' : 'Save Changes'}</button>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  /* ── VIEW: LIST (Screenshot 1 — default) ── */
  return (
    <AdminLayout>
      <div className="rp-page">
        <div className="rp-page-header">
          <div>
            <h1 className="rp-title">Sub Admin Account Manage</h1>
            <p className="rp-subtitle">Add, edit, and control sub-admin accounts with customized roles and permissions.</p>
          </div>
          <button className="rp-add-btn" onClick={() => { setView('create'); setCreateForm({ email: '', password: '' }); setCreateError(''); }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 5V19" stroke="#fff" strokeWidth="2" strokeLinecap="round"/><path d="M5 12H19" stroke="#fff" strokeWidth="2" strokeLinecap="round"/></svg>
            Add New Role
          </button>
        </div>

        {success && <div className="rp-alert rp-alert-success">{success}<button onClick={() => setSuccess('')}>&times;</button></div>}
        {error && <div className="rp-alert rp-alert-error">{error}</div>}

        <div className="rp-section">
          <h2 className="rp-section-title">Added Accounts</h2>

          {loading ? (
            <div className="rp-loading">Loading accounts...</div>
          ) : accounts.length === 0 ? (
            <div className="rp-empty">No sub-admin accounts found. Click "Add New Role" to create one.</div>
          ) : (
            <div className="rp-accounts-card">
              {accounts.map((u) => {
                const imgUrl = getFullImageUrl(u.avatar);
                const displayName = u.companyName || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email?.split('@')[0] || 'User';
                const isActive = (u.status || 'active').toLowerCase() === 'active';
                const phone = u.phone || '+00 000 000 00';

                return (
                  <div key={u.id} className="rp-account-row">
                    {/* Avatar */}
                    <div className="rp-cell rp-cell-avatar">
                      {imgUrl ? (
                        <img src={imgUrl} alt="" className="rp-avatar-img" onError={(e) => { e.target.style.display = 'none'; if(e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }} />
                      ) : null}
                      <span className="rp-avatar-fallback" style={imgUrl ? { display: 'none' } : {}}>
                        {(displayName[0] || '?').toUpperCase()}
                      </span>
                    </div>

                    {/* Name */}
                    <div className="rp-cell rp-cell-name">{displayName}</div>

                    {/* Email */}
                    <div className="rp-cell rp-cell-email">{u.email}</div>

                    {/* Phone */}
                    <div className="rp-cell rp-cell-phone">{phone}</div>

                    {/* Change Password */}
                    <div className="rp-cell rp-cell-password">
                      <button className="rp-change-pw-btn" onClick={() => { setChangePwUser(u); setNewPassword(''); }}>Change Password</button>
                    </div>

                    {/* Copy */}
                    <div className="rp-cell rp-cell-copy">
                      <button className={`rp-copy-btn ${copiedId === u.id ? 'copied' : ''}`} onClick={() => handleCopy(u)}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="1.5"/><path d="M5 15H4C2.895 15 2 14.105 2 13V4C2 2.895 2.895 2 4 2H13C14.105 2 15 2.895 15 4V5" stroke="currentColor" strokeWidth="1.5"/></svg>
                        <span>Copied</span>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </button>
                    </div>

                    {/* Status */}
                    <div className="rp-cell rp-cell-status">
                      <button
                        className={`rp-status-badge ${isActive ? 'active' : 'inactive'}`}
                        style={{ cursor: 'pointer', border: 'none', background: isActive ? '#E8F5E9' : '#FFF3E0' }}
                        title={isActive ? 'Click to deactivate' : 'Click to activate'}
                        onClick={() => toggleStatus(u)}
                      >
                        {isActive ? 'Active' : 'Inactive'}
                      </button>
                    </div>

                    {/* Permission */}
                    <div className="rp-cell rp-cell-perm">
                      <button className="rp-perm-btn" onClick={() => openPermissions(u)}>
                        Permission
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                      </button>
                    </div>

                    {/* Actions */}
                    <div className="rp-cell rp-cell-actions">
                      <button className="rp-action-icon" title="View Details" onClick={() => setViewUser(u)}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M2 12C2 12 5 5 12 5C19 5 22 12 22 12C22 12 19 19 12 19C5 19 2 12 2 12Z" stroke="#6D6D6D" strokeWidth="1.5" strokeLinecap="round"/><circle cx="12" cy="12" r="3" stroke="#6D6D6D" strokeWidth="1.5"/></svg>
                      </button>
                      <button className="rp-action-icon rp-action-delete" title="Delete" onClick={() => handleDelete(u)}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M3 6H5H21" stroke="#DC3545" strokeWidth="1.5" strokeLinecap="round"/><path d="M8 6V4C8 3.448 8.448 3 9 3H15C15.552 3 16 3.448 16 4V6M19 6V20C19 20.552 18.552 21 18 21H6C5.448 21 5 20.552 5 20V6H19Z" stroke="#DC3545" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Change Password Modal */}
        {changePwUser && (
          <div className="rp-modal-overlay" onClick={() => setChangePwUser(null)}>
            <div className="rp-modal" onClick={(e) => e.stopPropagation()}>
              <div className="rp-modal-header">
                <h3>Change Password</h3>
                <button className="rp-modal-close" onClick={() => setChangePwUser(null)}>&times;</button>
              </div>
              <div className="rp-modal-body">
                <p style={{ color: '#6D6D6D', marginBottom: '16px' }}>Set new password for: <strong>{changePwUser.email}</strong></p>
                <div className="rp-form-group">
                  <label className="rp-label">New Password</label>
                  <input type="password" className="rp-input" placeholder="Enter new password (min 6 chars)" value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)} />
                </div>
                <div className="rp-form-actions" style={{ marginTop: '16px' }}>
                  <button className="rp-btn rp-btn-cancel" onClick={() => setChangePwUser(null)}>Cancel</button>
                  <button className="rp-btn rp-btn-save" onClick={handleChangePassword}>Save Password</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* View Details Modal */}
        {viewUser && (
          <div className="rp-modal-overlay" onClick={() => setViewUser(null)}>
            <div className="rp-modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
              <div className="rp-modal-header">
                <h3>Account Details</h3>
                <button className="rp-modal-close" onClick={() => setViewUser(null)}>&times;</button>
              </div>
              <div className="rp-modal-body">
                {/* Avatar + name */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
                  {getFullImageUrl(viewUser.avatar) ? (
                    <img src={getFullImageUrl(viewUser.avatar)} alt="" style={{ width: 52, height: 52, borderRadius: '50%', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#E8F0FE', color: '#1B7FE3', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 20 }}>
                      {((viewUser.companyName || viewUser.firstName || viewUser.email || 'U')[0]).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 16, color: '#1A1A1A' }}>{viewUser.companyName || `${viewUser.firstName || ''} ${viewUser.lastName || ''}`.trim() || viewUser.email?.split('@')[0]}</div>
                    <div style={{ fontSize: 13, color: '#6D6D6D', marginTop: 2 }}>{viewUser.adminRole === 'admin' ? 'Admin' : 'Sub Admin'}</div>
                  </div>
                </div>
                {/* Detail rows */}
                {[['Email', viewUser.email], ['Phone', viewUser.phone || '—'], ['Role', viewUser.role || '—'], ['Country', viewUser.country || '—'], ['Status', viewUser.status || 'active'], ['Email Verified', viewUser.isEmailVerified ? 'Yes' : 'No'], ['Created', viewUser.createdAt ? new Date(viewUser.createdAt).toLocaleDateString() : '—'], ['Last Login', viewUser.lastLoginAt ? new Date(viewUser.lastLoginAt).toLocaleDateString() : 'Never']].map(([label, value]) => (
                  <div key={label} style={{ display: 'flex', padding: '10px 0', borderBottom: '1px solid #F0F0F0', gap: 12 }}>
                    <span style={{ fontWeight: 600, color: '#4A4A4A', width: 120, flexShrink: 0, fontSize: 13 }}>{label}</span>
                    <span style={{ color: '#1A1A1A', fontSize: 13 }}>{value}</span>
                  </div>
                ))}
                {/* Permissions */}
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#1A1A1A', marginBottom: 10 }}>Permissions</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {PERMISSION_FIELDS.map(p => (
                      <span key={p.key} style={{ padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 500, background: viewUser[p.key] ? '#E8F5E9' : '#F3F3F3', color: viewUser[p.key] ? '#388E3C' : '#999' }}>
                        {p.label}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="rp-form-actions" style={{ marginTop: 20 }}>
                  <button className="rp-btn rp-btn-cancel" onClick={() => setViewUser(null)}>Close</button>
                  <button className="rp-btn rp-btn-save" onClick={() => { setViewUser(null); openPermissions(viewUser); }}>Edit Permissions</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default SubAdminPage;
